import React from "react";
import { Grid, Typography, CardHeader, CardContent } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { ChartCard } from "../Style/health-chart.style";

export default function ScreeningCharts({
    selectedDistrict,
    allDistrictsChartData,
    ageGroups
}) {
    // Prepare gender-wise age group data
    const genderAgeGroupData = selectedDistrict === 'ALL'
        ? {
            female: [
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_f_18_29 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_f_30_39 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_f_40_49 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_f_50_59 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_f_60 || 0), 0)
            ],
            male: [
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_m_18_29 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_m_30_39 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_m_40_49 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_m_50_59 || 0), 0),
                allDistrictsChartData.reduce((sum, d) => sum + Number(d.sex_m_60 || 0), 0)
            ]
        }
        : {
            female: [
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_f_18_29 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_f_30_39 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_f_40_49 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_f_50_59 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_f_60 || 0)
            ],
            male: [
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_m_18_29 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_m_30_39 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_m_40_49 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_m_50_59 || 0),
                Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_m_60 || 0)
            ]
        };

    // Prepare gender-wise total screening data
    const genderScreeningData = selectedDistrict === 'ALL'
        ? allDistrictsChartData.map(d => ({
            district: d.DISTRICT,
            female: Number(d.count_sex_f || 0),
            male: Number(d.count_screenings || 0) - Number(d.count_sex_f || 0)
        }))
        : [{
            district: selectedDistrict,
            female: Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.count_sex_f || 0),
            male: Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.count_screenings || 0) -
                   Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.count_sex_f || 0)
        }];

    // Sort gender screening data by total count (low to high)
    const sortedGenderScreeningData = genderScreeningData.sort((a, b) =>
        (a.female + a.male) - (b.female + b.male)
    );

    return (
        <>
            <Grid item xs={12} md={6} sx={{ width: "450px" }}>
                <ChartCard>
                    <CardHeader
                        title={
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", fontSize: "14px", textAlign: 'center', textDecorationLine: 'underline' }}
                            >
                                Age wise screening of male/female
                            </Typography>
                        }
                        sx={{ pb: 0 }}
                    />
                    <CardContent>
                        <BarChart
                            xAxis={[{ scaleType: "linear" }]}
                            yAxis={[
                                {
                                    width: 50,
                                    scaleType: "band",
                                    data: ageGroups,
                                    tickLabelStyle: { fontSize: 12 },
                                },
                            ]}
                            series={[
                                {
                                    data: genderAgeGroupData.male,
                                    label: "Male",
                                    color: "#2196f3",
                                },
                                {
                                    data: genderAgeGroupData.female,
                                    label: "Female",
                                    color: "#ff69b4",
                                },
                            ]}
                            layout="horizontal"
                            height={455}
                            slotProps={{
                                legend: {
                                    direction: "horizontal",
                                    position: { horizontal: "center", vertical: "bottom" },
                                },
                            }}
                        />
                    </CardContent>
                </ChartCard>
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "550px" }}>
                <ChartCard>
                    <CardHeader
                        title={
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", fontSize: "14px", textAlign: 'center', textDecorationLine: 'underline' }}
                            >
                                District wise total screening of male/female
                            </Typography>
                        }
                        sx={{ pb: 0 }}
                    />
                    <CardContent>
                        <BarChart
                            xAxis={[{ scaleType: "linear" }]}
                            yAxis={[
                                {
                                    width: 100,
                                    scaleType: "band",
                                    data: sortedGenderScreeningData.map(item => item.district),
                                    tickLabelStyle: { fontSize: 12 },
                                },
                            ]}
                            series={[
                                {
                                    data: sortedGenderScreeningData.map(item => item.female),
                                    label: "Female",
                                    color: "#ff69b4",
                                },
                                {
                                    data: sortedGenderScreeningData.map(item => item.male),
                                    label: "Male",
                                    color: "#2196f3",
                                },
                            ]}
                            layout="horizontal"
                            height={455}
                            slotProps={{
                                legend: {
                                    direction: "horizontal",
                                    position: { horizontal: "center", vertical: "bottom" },
                                },
                            }}
                        />
                    </CardContent>
                </ChartCard>
            </Grid>
        </>
    );
}
